import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { workflowAPI } from '../../lib/api';
import { CHANNEL_META, errMsg } from '../../lib/workflow';
import { QueueSection, StatTile } from '../workflow/QueueSection';
import { QueueCard } from '../workflow/QueueCard';
import { ChannelChips } from '../workflow/ChannelChips';
import { HomeHeader } from './HomeHeader';

export const UtkarshHome = ({ data, user, embedded }) => {
  const { summary, items, reload } = data;
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const queue = items.filter((i) => i.stage === 'live_on_etsy');
  const done = items.filter((i) => i.stage === 'done').slice(0, 6);
  const pending = queue.reduce(
    (n, it) => n + Object.values(it.channels || {}).filter((c) => !['done', 'skipped'].includes(c.status)).length, 0,
  );
  const open = (id, ch) => navigate(`/etsy/${id}?mp=${CHANNEL_META[ch].mp}`);

  const complete = async (it) => {
    setBusyId(it.id);
    try {
      await workflowAPI.complete(it.id);
      toast.success('Listing complete on every channel 🎉');
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Could not complete'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="utkarsh-home">
      {!embedded && (
        <HomeHeader emoji="🌐" name={user?.name} subtitle="These are live on Etsy. Take them to TLJ, eBay, Walmart, Amazon, Pinterest & resellers. Tap a channel to open it or tick it off ✅" />
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile emoji="🛍️" value={queue.length} label="Ready for channels" testId="utkarsh-stat-queue" tone="bg-[#ECFDF5]/60" />
        <StatTile emoji="⏳" value={pending} label="Channel pushes left" testId="utkarsh-stat-pending" />
        <StatTile emoji="✅" value={summary?.today?.channel_done || 0} label="Ticked off today" testId="utkarsh-stat-ticked-today" />
        <StatTile emoji="🎉" value={summary?.today?.completed || 0} label="Completed today" testId="utkarsh-stat-completed-today" tone="bg-[#F0FDF4]" />
      </div>
      <QueueSection
        emoji="🚀" title="Live on Etsy: ready for other channels" testId="utkarsh-live-queue"
        subtitle="Channels pushed from the panels are detected automatically. Tick or skip the rest."
        items={queue} empty="Nothing waiting. Nayan's next Etsy-live listings land here 🎈" cols="2xl:grid-cols-2"
        render={(it, i) => (
          <QueueCard
            key={it.id} item={it} index={i} showNotes={false}
            footer={<ChannelChips item={it} canEdit onOpen={(ch) => open(it.id, ch)} onChanged={reload} />}
          >
            {it.channels_complete && (
              <Button onClick={() => complete(it)} disabled={busyId === it.id} className="h-8 rounded-xl text-xs font-bold bg-[#22C55E] hover:bg-[#16A34A] text-white" data-testid={`utkarsh-complete-button-${it.id}`}>
                Mark complete 🎉
              </Button>
            )}
          </QueueCard>
        )}
      />
      <QueueSection
        emoji="🎉" title="Recently completed" testId="utkarsh-done-queue"
        items={done} empty="Completed listings show up here"
        render={(it, i) => <QueueCard key={it.id} item={it} index={i} showNotes={false} />}
      />
    </div>
  );
};
